import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Wallet, Landmark, ShoppingCart, RefreshCw } from 'lucide-react';
import BottomNav from '../components/BottomNav';
import SendCryptoListModal from '../components/SendCryptoListModal';
import SendTransferModal from '../components/SendTransferModal';

// Hide scrollbar but keep functionality
const scrollbarHide = "[-ms-overflow-style:'none'] [scrollbar-width:'none'] [&::-webkit-scrollbar]:hidden";

const ActionButton = ({ icon: Icon, label, onClick }) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center justify-center px-5 py-4 min-w-[90px] flex-shrink-0
               bg-white/10 backdrop-blur-sm text-white rounded-2xl shadow-lg hover:bg-white/20
               transition-all border border-white/20"
  >
    <Icon className="w-8 h-8 mb-2" />
    <span className="text-xs font-semibold tracking-wide">{label}</span>
  </button>
);

const CryptoDetailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [cryptoData, setCryptoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [change24h, setChange24h] = useState(0);

  // Local modal state for selecting and sending crypto
  const [showList, setShowList] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [selectedCrypto, setSelectedCrypto] = useState(null);

  const id = searchParams.get('id')?.toLowerCase();
  const name = searchParams.get('name');
  const cryptoName = name || (id ? id.charAt(0).toUpperCase() + id.slice(1) : 'Crypto');

  const fetchCryptoData = async () => {
    setLoading(true);
    setError(null);
    if (!id) return setError('No coin selected');

    const API_KEY = 'CG-EnG3bBe1GkK46FFWpbmBQL5s'; 
    try {
      const res = await fetch(
        `https://api.coingecko.com/api/v3/coins/${id}?localization=false&market_data=true&x_cg_demo_api_key=${API_KEY}`
      );
      if (!res.ok) throw new Error(`API Error ${res.status}`);
      const data = await res.json();
      setCryptoData(data);
      setChange24h(data.market_data.price_change_percentage_24h || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCryptoData(); 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSelectCrypto = (crypto) => {
    setSelectedCrypto(crypto);
    setShowTransfer(true);
  };

  return (
    <>
      {/* Full Screen Scroll Area – Respects Top & Bottom Navs */}
      <div 
        className="min-h-screen bg-gradient-to-br from-[#1a1033] via-[#1F2937] to-[#1a1033]
                   pt-[max(4rem,env(safe-area-inset-top))] 
                   pb-[max(5rem,env(safe-area-inset-bottom))] 
                   px-4 sm:px-6"
      >
        <div className="max-w-4xl mx-auto">

          {/* Main Glassmorphic Card */}
          <div className="bg-white/5 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/10 p-6 sm:p-8">

            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
              <div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight">
                  {cryptoName}
                </h1>
                <p className="text-white/70 text-base sm:text-lg mt-2 font-medium">Live Market Overview</p>
              </div>
              <button
                onClick={fetchCryptoData}
                className="p-3 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl hover:scale-105 transition shadow-lg"
              >
                <RefreshCw className={`w-6 h-6 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* Action Buttons – Always Horizontal */}
            <div className={`flex gap-4 ${scrollbarHide} overflow-x-auto pb-4 -mx-6 px-6 mb-10`}>
              <ActionButton icon={Landmark} label="Deposit" onClick={() => navigate('/fund')} />
              <ActionButton icon={Wallet} label="Withdraw" onClick={() => setShowList(true)} />
              <a href="https://youtu.be/4D8S5rU8NoE?si=H3BPD34_o13MG0Ko" target="_blank" rel="noopener noreferrer" className="inline-block">
                <ActionButton icon={ShoppingCart} label="Buy" />
             </a>
            </div>

            {/* Stats Grid – Super Responsive */}
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-purple-500 border-r-4 border-r-transparent"></div>
              </div>
            ) : error ? (
              <div className="text-center py-16">
                <p className="text-red-400 text-xl font-bold">Failed to Load</p>
                <p className="text-white/60 mt-2">{error}</p>
              </div>
            ) : cryptoData ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {[
                  { label: "Price", value: `$${cryptoData.market_data.current_price.usd.toLocaleString()}` },
                  { label: "24h Change", value: `${change24h.toFixed(2)}%`, color: change24h >= 0 ? "text-green-400" : "text-red-400" },
                  { label: "Market Cap", value: `$${cryptoData.market_data.market_cap.usd.toLocaleString()}` },
                  { label: "24h Volume", value: `$${cryptoData.market_data.total_volume.usd.toLocaleString()}` },
                  { label: "24h High/Low", value: `$${cryptoData.market_data.high_24h.usd.toLocaleString()} / $${cryptoData.market_data.low_24h.usd.toLocaleString()}` },
                  { label: "Rank", value: `#${cryptoData.market_cap_rank}`, color: "text-yellow-400" },
                ].map((item) => (
                  <div key={item.label} className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/10 hover:bg-white/15 transition">
                    <p className="text-white/60 text-sm font-medium">{item.label}</p>
                    <p className={`text-2xl font-bold mt-2 ${item.color || "text-white"}`}>
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>
            ) : null}

            <p className="text-center text-white/40 text-xs mt-12 tracking-wider">
              Powered by CoinGecko 
            </p>
          </div>
        </div>
      </div>

      {/* Fixed Bottom Navigation */}
      <div className="fixed inset-x-0 bottom-0 z-50">
        <BottomNav />
      </div>

      {/* Send modals */}
      <SendCryptoListModal
        isOpen={showList}
        onClose={() => setShowList(false)}
        onSelectCrypto={handleSelectCrypto}
      />
      <SendTransferModal
        isOpen={showTransfer}
        onClose={() => {
          setShowTransfer(false);
          setSelectedCrypto(null);
        }}
        crypto={selectedCrypto}
      />
    </>
  );
};

export default CryptoDetailPage;